import {
    schedulingHoursBlockAriaLabel,
    startTimeAriaLabel,
    endTimeAriaLabel,
    removeThisTimeSlotAriaLabel,
    addNewTimeSlotAriaLabel,
} from './SchedulingHours.locstring.json';
import loc from 'owa-localize';
import { observer } from 'owa-mobx-react';
import React from 'react';
import cx from 'owa-classnames';
import { Icon } from '@fluentui/react/lib/Icon';
import type { IDropdownStyles } from '@fluentui/react/lib/Dropdown';
import { Dropdown } from '@fluentui/react/lib/Dropdown';
import { Label } from '@fluentui/react/lib/Label';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports  -- (https://aka.ms/OWALintWiki)
 * Baseline - Migrating deprecated icons: https://outlookweb.visualstudio.com/Outlook%20Web/_wiki/wikis/Outlook%20Web.wiki/9297/Migrating-deprecated-icons-in-client-web
 *	> 'ControlIcons' import from owa-control-icons is restricted. Use JSX icons from @fluentui/react-icons, otherwise from owa-fluent-icons-svg if they can only be reference by string. */
import { type ControlIcons } from 'owa-control-icons';
import type { ITimeListItem } from '../utils/generateTimeList';
import { generateTimeList } from '../utils/generateTimeList';
import { dropDownStyles, labelStyles } from '../utils/styles';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- (https://aka.ms/OWALintWiki)
 * Baseline. DO NOT COPY AND PASTE!
 *	> '../constants/index' import is restricted from being used by a pattern. Do not import from an index file directly. Import from the specific file instead. */
import {
    DEFAULT_START_TIME,
    DEFAULT_END_TIME,
    DEFAULT_TIME_INTERVAL,
    DEFAULT_BUSINESS_START_TIME,
    DEFAULT_BUSINESS_END_TIME,
    getWeekdayNames,
    getShortWeekdayNames,
} from '../constants/index';
import {
    row,
    day,
    inheritFont as inlineStyles_inheritFont,
    disabled,
    enableMobileMode as inlineStyles_enableMobileMode,
    webLabel,
    mobileLabel,
    startTime,
    endTime,
    buttonWrapper,
    icon,
    nonWorkingDay,
} from './SchedulingHours.scss';

export interface ITimeSlot {
    startMinutes: number;
    endMinutes: number;
}
export interface IWorkDay {
    index: number;
    // TODO: This is no longer used and should be cleaned up from all references
    dayText?: string;
    timeSlots: ITimeSlot[];
}
export interface SchedulingHoursProps {
    schedulingHours: IWorkDay[];
    nonWorkingDayText: string;
    ariaLabel?: string;
    updateSchedulingHours: (updatedSchedulingHours: any) => void;
    defaultStartTime?: number;
    defaultEndTime?: number;
    defaultTimeInterval?: number;
    defaultBusinessStartTime?: number;
    defaultBusinessEndTime?: number;
    maxTimeslotsPerDay?: number;
    inheritFont?: boolean;
    isDisabled?: boolean;
    timePickerWidth?: number;
    nonWorkingDaysWidth?: number;
    newDropdownStyles?: Partial<IDropdownStyles>;
    enableMobileMode?: boolean;
}

const calloutProps = { calloutMaxHeight: 290 };

/* eslint-disable-next-line owa-custom-rules/prefer-react-functional-components -- (https://aka.ms/OWALintWiki)
 * This react class component should be re-factored as functional component, if is not possible, write a justification.
 *	> When implementing a simple component, write it as a functional component rather than as a class component */
class SchedulingHours extends React.Component<SchedulingHoursProps, {}> {
    private START_TIMELIST: ITimeListItem[];
    private END_TIMELIST: ITimeListItem[];
    private addButtonRefs: HTMLButtonElement[] = [];

    constructor(props: SchedulingHoursProps) {
        super(props);

        const {
            defaultStartTime = DEFAULT_START_TIME,
            defaultEndTime = DEFAULT_END_TIME,
            defaultTimeInterval = DEFAULT_TIME_INTERVAL,
        } = this.props;

        this.START_TIMELIST = generateTimeList(
            defaultStartTime,
            defaultEndTime - defaultTimeInterval,
            defaultTimeInterval
        );
        this.END_TIMELIST = generateTimeList(
            defaultStartTime + defaultTimeInterval,
            defaultEndTime,
            defaultTimeInterval
        );
    }

    private addNewTimeSlot = (
        dayIndex: number,
        timeSlotIndex: number,
        defaultBusinessStartTime: number,
        defaultBusinessEndTime: number
    ): void => {
        if (this.props.isDisabled) {
            return;
        }
        const schedulingHours = this.props.schedulingHours;
        const updatedSchedulingHours = schedulingHours.map(bh => {
            if (bh.index == dayIndex) {
                // concatenating new time slot and returning
                // in updatedSchedulingHours
                const cbh = { ...bh };
                cbh.timeSlots = bh.timeSlots.concat({
                    startMinutes: defaultBusinessStartTime,
                    endMinutes: defaultBusinessEndTime,
                });
                return cbh;
            }
            return bh;
        });
        // pass updated scheduling hours back to parent
        this.props.updateSchedulingHours(updatedSchedulingHours);
        /* eslint-disable-next-line no-restricted-globals -- (https://aka.ms/OWALintWiki)
         * BASELINE. DO NOT COPY AND PASTE!
         *	> Unexpected use of 'requestAnimationFrame'. Use safeRequestAnimationFrame in owa-performance instead. */
        requestAnimationFrame(() => {
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2531 (104,13): Object is possibly 'null'.
            // @ts-expect-error
            document.getElementById(`day${dayIndex}_startTimeSlot${timeSlotIndex + 1}`).focus();
        });
    };

    private removeTimeSlot = (timeSlotindex: number, dayIndex: number): void => {
        if (this.props.isDisabled) {
            return;
        }
        const schedulingHours = this.props.schedulingHours;
        const updatedSchedulingHours = schedulingHours.map(bh => {
            if (bh.index == dayIndex) {
                // splicing the removed time slot and returning
                // in updatedSchedulingHours
                const cbh = { ...bh };
                cbh.timeSlots = bh.timeSlots.concat([]);
                cbh.timeSlots.splice(timeSlotindex, 1);
                return cbh;
            }
            return bh;
        });
        // pass updated scheduling hours back to parent
        this.props.updateSchedulingHours(updatedSchedulingHours);
        /* eslint-disable-next-line no-restricted-globals -- (https://aka.ms/OWALintWiki)
         * BASELINE. DO NOT COPY AND PASTE!
         *	> Unexpected use of 'requestAnimationFrame'. Use safeRequestAnimationFrame in owa-performance instead. */
        requestAnimationFrame(() => {
            const elem = this.addButtonRefs[dayIndex];
            elem.focus();
        });
    };

    private updateTimeSlot = (
        timeSlotindex: number,
        dayIndex: number,
        type: string,
        _event: React.FormEvent<HTMLDivElement>,
        selectedTimeSlot: any
    ): void => {
        if (this.props.isDisabled) {
            return;
        }
        const selectedTime = selectedTimeSlot.key;
        const schedulingHours = this.props.schedulingHours;
        const updatedSchedulingHours = schedulingHours.map(bh => {
            if (bh.index == dayIndex) {
                // updating the time slot and returning
                // in updatedSchedulingHours
                const cbh = { ...bh };
                cbh.timeSlots = bh.timeSlots.concat([]);
                cbh.timeSlots[timeSlotindex] = {
                    ...cbh.timeSlots[timeSlotindex],
                    [type]: selectedTime,
                };
                return cbh;
            }
            return bh;
        });
        this.props.updateSchedulingHours(updatedSchedulingHours);
    };

    render(): React.JSX.Element {
        let {
            schedulingHours,
            defaultBusinessStartTime,
            defaultBusinessEndTime,
            nonWorkingDayText,
            ariaLabel,
            maxTimeslotsPerDay,
            inheritFont,
            isDisabled,
            timePickerWidth,
            nonWorkingDaysWidth,
        } = this.props;
        defaultBusinessStartTime = defaultBusinessStartTime || DEFAULT_BUSINESS_START_TIME;
        defaultBusinessEndTime = defaultBusinessEndTime || DEFAULT_BUSINESS_END_TIME;

        return (
            <div tabIndex={0} aria-label={loc(schedulingHoursBlockAriaLabel)}>
                {schedulingHours &&
                    schedulingHours.map(sh => (
                        <div key={sh.index} className={row}>
                            {sh.timeSlots.map((timeSlot, index) => (
                                <div key={index}>
                                    <div
                                        className={cx({
                                            [`${day}`]: true,
                                            [`${inlineStyles_inheritFont}`]: inheritFont,
                                            [`${disabled}`]: isDisabled,
                                            [`${inlineStyles_enableMobileMode}`]:
                                                this.props.enableMobileMode,
                                        })}
                                        tabIndex={index == 0 ? 0 : -1}
                                        aria-label={getWeekdayNames()[sh.index]}
                                    >
                                        {index == 0 ? (
                                            <>
                                                {!this.props.enableMobileMode && (
                                                    <Label>{getWeekdayNames()[sh.index]}</Label>
                                                )}
                                                {this.props.enableMobileMode && (
                                                    <>
                                                        <Label className={webLabel}>
                                                            {getWeekdayNames()[sh.index]}
                                                        </Label>
                                                        <Label className={mobileLabel}>
                                                            {getShortWeekdayNames()[sh.index]}
                                                        </Label>
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                    <div
                                        className={startTime}
                                        /* eslint-disable-next-line react-perf/jsx-no-new-object-as-prop  -- (https://aka.ms/OWALintWiki)
                                         * Causes extra re renders of component
                                         *	> JSX attribute values should not contain objects created in the same scope */
                                        style={{ width: timePickerWidth || 100 }}
                                    >
                                        <Dropdown
                                            tabIndex={0}
                                            selectedKey={timeSlot.startMinutes}
                                            ariaLabel={loc(startTimeAriaLabel)}
                                            options={this.START_TIMELIST}
                                            calloutProps={calloutProps}
                                            styles={this.props.newDropdownStyles || dropDownStyles}
                                            id={`day${sh.index}_startTimeSlot${index}`}
                                            /* eslint-disable-next-line react-perf/jsx-no-new-function-as-prop  -- (https://aka.ms/OWALintWiki)
                                             * Baseline, please do not copy and paste this justification
                                             *	> JSX attribute values should not contain functions created in the same scope */
                                            onChange={this.updateTimeSlot.bind(
                                                this,
                                                index,
                                                sh.index,
                                                'startMinutes'
                                            )}
                                            disabled={isDisabled}
                                        />
                                    </div>
                                    <div
                                        className={endTime}
                                        /* eslint-disable-next-line react-perf/jsx-no-new-object-as-prop  -- (https://aka.ms/OWALintWiki)
                                         * Causes extra re renders of component
                                         *	> JSX attribute values should not contain objects created in the same scope */
                                        style={{ width: timePickerWidth || 102 }}
                                    >
                                        <Dropdown
                                            tabIndex={0}
                                            selectedKey={timeSlot.endMinutes}
                                            ariaLabel={loc(endTimeAriaLabel)}
                                            options={this.END_TIMELIST}
                                            calloutProps={calloutProps}
                                            styles={this.props.newDropdownStyles || dropDownStyles}
                                            /* eslint-disable-next-line react-perf/jsx-no-new-function-as-prop  -- (https://aka.ms/OWALintWiki)
                                             * Baseline, please do not copy and paste this justification
                                             *	> JSX attribute values should not contain functions created in the same scope */
                                            onChange={this.updateTimeSlot.bind(
                                                this,
                                                index,
                                                sh.index,
                                                'endMinutes'
                                            )}
                                            disabled={isDisabled}
                                        />
                                    </div>

                                    <div
                                        className={buttonWrapper}
                                        /* eslint-disable-next-line react-perf/jsx-no-new-function-as-prop  -- (https://aka.ms/OWALintWiki)
                                         * Baseline, please do not copy and paste this justification
                                         *	> JSX attribute values should not contain functions created in the same scope */
                                        onClick={this.removeTimeSlot.bind(this, index, sh.index)}
                                    >
                                        {/* eslint-disable-next-line react/button-has-type -- (https://aka.ms/OWALintWiki)
                                         * Baseline, DO NOT COPY-PASTE!, change this line with a justification or add proper type prop to the button between "button", "submit", or "reset" https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/button-has-type.md
                                         *	> Missing an explicit type attribute for button */}
                                        <button
                                            title={loc(removeThisTimeSlotAriaLabel)}
                                            className={cx({
                                                [`${disabled}`]: isDisabled,
                                            })}
                                            aria-label={loc(removeThisTimeSlotAriaLabel)}
                                            disabled={isDisabled}
                                        >
                                            <Icon
                                                iconName={'Clear'}
                                                className={cx({
                                                    [`${icon}`]: true,
                                                    [`${disabled}`]: isDisabled,
                                                })}
                                            />
                                        </button>
                                    </div>
                                    {index === sh.timeSlots.length - 1 &&
                                        (!maxTimeslotsPerDay || index < maxTimeslotsPerDay - 1) && (
                                            <div
                                                className={buttonWrapper}
                                                /* eslint-disable-next-line react-perf/jsx-no-new-function-as-prop  -- (https://aka.ms/OWALintWiki)
                                                 * Baseline, please do not copy and paste this justification
                                                 *	> JSX attribute values should not contain functions created in the same scope */
                                                onClick={this.addNewTimeSlot.bind(
                                                    this,
                                                    sh.index,
                                                    index,
                                                    defaultBusinessStartTime,
                                                    defaultBusinessEndTime
                                                )}
                                            >
                                                {/* eslint-disable-next-line react/button-has-type -- (https://aka.ms/OWALintWiki)
                                                 * Baseline, DO NOT COPY-PASTE!, change this line with a justification or add proper type prop to the button between "button", "submit", or "reset" https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/button-has-type.md
                                                 *	> Missing an explicit type attribute for button */}
                                                <button
                                                    /* eslint-disable-next-line react-perf/jsx-no-new-function-as-prop  -- (https://aka.ms/OWALintWiki)
                                                     * Baseline, please do not copy and paste this justification
                                                     *	> JSX attribute values should not contain functions created in the same scope */
                                                    ref={ref =>
                                                        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                                                        // -> Error TS2322 (276,58): Type 'HTMLButtonElement | null' is not assignable to type 'HTMLButtonElement'.
                                                        // @ts-expect-error
                                                        (this.addButtonRefs[sh.index] = ref)
                                                    }
                                                    title={`${loc(addNewTimeSlotAriaLabel)} ${
                                                        getWeekdayNames()[sh.index]
                                                    }`}
                                                    aria-label={`${loc(addNewTimeSlotAriaLabel)} ${
                                                        getWeekdayNames()[sh.index]
                                                    }`}
                                                    className={cx({
                                                        [`${disabled}`]: isDisabled,
                                                    })}
                                                    disabled={isDisabled}
                                                >
                                                    <Icon
                                                        iconName={'Add'}
                                                        className={cx({
                                                            [`${icon}`]: true,
                                                            [`${disabled}`]: isDisabled,
                                                        })}
                                                    />
                                                </button>
                                            </div>
                                        )}
                                </div>
                            ))}
                            {sh.timeSlots && sh.timeSlots.length == 0 ? (
                                <div>
                                    <div
                                        className={cx({
                                            [`${day}`]: true,
                                            [`${inlineStyles_inheritFont}`]: inheritFont,
                                            [`${disabled}`]: isDisabled,
                                            [`${inlineStyles_enableMobileMode}`]:
                                                this.props.enableMobileMode,
                                        })}
                                        tabIndex={0}
                                        aria-label={getWeekdayNames()[sh.index]}
                                    >
                                        {/* <Label>{CONSTANTS.getWeekdayNames()[sh.index]} </Label> */}
                                        {!this.props.enableMobileMode && (
                                            <Label>{getWeekdayNames()[sh.index]}</Label>
                                        )}
                                        {this.props.enableMobileMode && (
                                            <>
                                                <Label className={webLabel}>
                                                    {getWeekdayNames()[sh.index]}
                                                </Label>
                                                <Label className={mobileLabel}>
                                                    {getShortWeekdayNames()[sh.index]}
                                                </Label>
                                            </>
                                        )}
                                    </div>
                                    <div
                                        className={cx({
                                            [`${nonWorkingDay}`]: true,
                                            [`${disabled}`]: isDisabled,
                                        })}
                                        tabIndex={0}
                                        aria-label={ariaLabel ?? nonWorkingDayText}
                                        /* eslint-disable-next-line react-perf/jsx-no-new-object-as-prop  -- (https://aka.ms/OWALintWiki)
                                         * Causes extra re renders of component
                                         *	> JSX attribute values should not contain objects created in the same scope */
                                        style={{
                                            minWidth: nonWorkingDaysWidth || 209,
                                            maxWidth: nonWorkingDaysWidth || 245,
                                        }}
                                    >
                                        <Label styles={labelStyles}>{nonWorkingDayText}</Label>
                                    </div>
                                    <div
                                        className={buttonWrapper}
                                        /* eslint-disable-next-line react-perf/jsx-no-new-function-as-prop  -- (https://aka.ms/OWALintWiki)
                                         * Baseline, please do not copy and paste this justification
                                         *	> JSX attribute values should not contain functions created in the same scope */
                                        onClick={this.addNewTimeSlot.bind(
                                            this,
                                            sh.index,
                                            -1,
                                            defaultBusinessStartTime,
                                            defaultBusinessEndTime
                                        )}
                                    >
                                        {/* eslint-disable-next-line react/button-has-type -- (https://aka.ms/OWALintWiki)
                                         * Baseline, DO NOT COPY-PASTE!, change this line with a justification or add proper type prop to the button between "button", "submit", or "reset" https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/button-has-type.md
                                         *	> Missing an explicit type attribute for button */}
                                        <button
                                            /* eslint-disable react-perf/jsx-no-new-function-as-prop --
                                             * Baseline, please do not copy and paste this justification
                                             *	> JSX attribute values should not contain functions created in the same scope */
                                            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                                            // -> Error TS2322 (341,58): Type 'HTMLButtonElement | null' is not assignable to type 'HTMLButtonElement'.
                                            // @ts-expect-error
                                            ref={ref => (this.addButtonRefs[sh.index] = ref)}
                                            /* eslint-enable react-perf/jsx-no-new-function-as-prop */
                                            title={`${loc(addNewTimeSlotAriaLabel)} ${
                                                getWeekdayNames()[sh.index]
                                            }`}
                                            aria-label={`${loc(addNewTimeSlotAriaLabel)} ${
                                                getWeekdayNames()[sh.index]
                                            }`}
                                            className={cx({
                                                [`${disabled}`]: isDisabled,
                                            })}
                                            disabled={isDisabled}
                                        >
                                            <Icon
                                                iconName={'Add'}
                                                className={cx({
                                                    [`${icon}`]: true,
                                                    [`${disabled}`]: isDisabled,
                                                })}
                                            />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                ''
                            )}
                        </div>
                    ))}
            </div>
        );
    }
}

const defaultExport: typeof SchedulingHours = observer(SchedulingHours);
export default defaultExport;
